import type { Category } from '@scayle/storefront-nuxt'
import type { Ref } from 'vue'
import { useRpcCall } from '#storefront/composables'
import { findCategoryById } from '../utils/category'
import type { SbMainNavigation } from '~/cms/storyblok/types/storyblok.d.ts'

/**
 * Number of descendant levels the main-navigation flyout renders below a
 * top-level entry (children as column headers + their children as lists).
 * A referenced category must carry at least this depth of children to render
 * a complete dropdown.
 */
const REQUIRED_CHILD_DEPTH = 2

/**
 * Resolves the categories that back the main navigation.
 *
 * The header fetches only the root category tree (`getRootCategories`, a few
 * levels deep). A `MainNavigation` entry may reference a non-root category,
 * whose subtree is either missing from that tree or truncated by the fetch
 * depth. For those references we fetch the category subtree on demand and
 * merge it in, so the flyout renders the full set of children regardless of
 * where the category sits in the tree.
 *
 * Root and shallow references are served entirely from the already-fetched
 * tree and trigger no extra requests.
 */
export function useMainNavigationCategories(
  mainNavigation: Ref<SbMainNavigation[] | undefined>,
  rootCategories: Ref<Category[] | undefined>,
) {
  const getCategoryById = useRpcCall('getCategoryById')

  const hasChildDepth = (category: Category, depth: number): boolean => {
    if (depth <= 0) return true
    return (category.children ?? []).some((child) =>
      hasChildDepth(child, depth - 1),
    )
  }

  // Referenced ids whose subtree is missing or too shallow in the root tree.
  const idsToFetch = computed(() => {
    const roots = rootCategories.value ?? []
    const ids = (mainNavigation.value ?? [])
      .map((item) => item.RootCategoryID)
      .filter((id): id is string => !!id)

    return [...new Set(ids)].filter((id) => {
      const found = findCategoryById(roots, id)
      return !found || !hasChildDepth(found, REQUIRED_CHILD_DEPTH)
    })
  })

  // `idsToFetch` derives from `mainNavigation` (the CMS nav story) and
  // `rootCategories`, both of which populate asynchronously after this
  // composable runs. On the initial server pass they may still be empty, so
  // this fetch can resolve to [] during SSR and only fill once the `watch`
  // re-runs it on the client. That is acceptable: the top-level nav entry is
  // rendered from the Storyblok item regardless, and only a referenced
  // category deeper than the root fetch depth relies on this data for its
  // dropdown — a case that does not exist while the tree is shallower than
  // NAVIGATION_DEPTH. If deep references ever need SSR-complete dropdowns,
  // move this fetch into the same awaited step that loads the nav story.
  const { data: fetchedCategories } = useAsyncData<Category[]>(
    'main-navigation-extra-categories',
    async () => {
      const ids = idsToFetch.value
      if (!ids.length) return []

      const results = await Promise.all(
        ids.map((id) =>
          getCategoryById({
            id: Number(id),
            children: REQUIRED_CHILD_DEPTH,
          }).catch(() => null),
        ),
      )

      return results.filter((category): category is Category => !!category)
    },
    { watch: [idsToFetch], default: () => [] },
  )

  // Prepend the on-demand categories so the recursive lookup in
  // useNavigationMapping resolves them (with their full subtree) before the
  // truncated copies nested inside the root tree.
  const categories = computed<Category[]>(() => [
    ...(fetchedCategories.value ?? []),
    ...(rootCategories.value ?? []),
  ])

  return { categories }
}
